import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError, recoveryBegin, recoveryVerify } from '../lib/api'

export function RecoveryPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [ticket, setTicket] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [totp, setTotp] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)

  async function onBegin(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setHint(null)
    setBusy(true)
    try {
      const res = await recoveryBegin(email.trim())
      setHint(res.message)
      if (res.ticket) setTicket(res.ticket)
      else setError(t('auth.recoveryNoTicket'))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.recoveryFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    if (!ticket) return
    setError(null)
    setBusy(true)
    try {
      await recoveryVerify({
        ticket,
        recovery_code: code.trim() || undefined,
        totp: totp.trim() || undefined,
      })
      navigate('/security/passkey', { replace: true, state: { fromRecovery: true } })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.recoveryFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="welcome card">
      <h1>{t('auth.recoveryTitle')}</h1>
      <p className="lede">{t('auth.recoveryLede')}</p>

      {!ticket ? (
        <form className="auth-form" onSubmit={onBegin}>
          <label className="field">
            <span>{t('auth.email')}</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {error ? (
            <p className="warn" role="alert">
              {error}
            </p>
          ) : null}
          {hint ? <p className="muted">{hint}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('auth.working') : t('auth.recoveryContinue')}
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={onVerify}>
          <p className="muted">{t('auth.recoveryTicketReady')}</p>
          <label className="field">
            <span>{t('auth.recoveryCode')}</span>
            <input
              type="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="xxxx-xxxx-xxxx"
            />
          </label>
          <label className="field">
            <span>{t('auth.totpOptional')}</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
          </label>
          {error ? (
            <p className="warn" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (!code.trim() && !totp.trim())}
          >
            {busy ? t('auth.working') : t('auth.recoveryVerify')}
          </button>
        </form>
      )}

      <p className="muted">
        <Link to="/passkey">{t('welcome.continuePasskey')}</Link>
        {' · '}
        <Link to="/welcome">{t('auth.backWelcome')}</Link>
      </p>
    </section>
  )
}
